/* FREDDY Pantroom — global behaviour (00-architecture §E.1).
   Replaces the concept app.js (menu + chooser), the global part of shop.js (mega menu, dialogs)
   and languages.js. Every listener is delegated or re-initialised on shopify:section:load, so
   theme-editor re-renders and freddy-cart.js swaps keep working. */
(() => {
  'use strict';

  const F = (window.FREDDY = window.FREDDY || {});
  const settings = F.settings || {};
  const $ = (selector, root = document) => root.querySelector(selector);
  const $$ = (selector, root = document) => Array.from(root.querySelectorAll(selector));
  const asElement = (target) => (target instanceof Element ? target : target && target.parentElement) || null;

  function dispatch(name, detail) {
    document.dispatchEvent(new CustomEvent(name, { detail }));
  }

  /* ---------- helpers exposed on FREDDY ---------- */

  function wrapperId(el) {
    const wrapper = el && el.closest ? el.closest('.shopify-section') : null;
    return wrapper && wrapper.id ? wrapper.id.replace(/^shopify-section-/, '') : null;
  }

  function decodeEntities(value) {
    const area = document.createElement('textarea');
    area.innerHTML = value;
    return area.value;
  }

  function formatMoney(cents, format) {
    let amount = cents;
    if (typeof amount === 'string') amount = amount.replace('.', '');
    amount = Number(amount);
    if (!Number.isFinite(amount)) amount = 0;
    const template = decodeEntities(String(format || settings.moneyFormat || '€ {{amount_with_comma_separator}}').replace(/<[^>]*>/g, ''));
    const number = (decimals, thousands = ',', decimal = '.') => {
      const fixed = (amount / 100).toFixed(decimals);
      const [whole, fraction] = fixed.split('.');
      const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return fraction ? grouped + decimal + fraction : grouped;
    };
    return template.replace(/\{\{\s*(\w+)\s*\}\}/, (match, key) => {
      switch (key) {
        case 'amount': return number(2);
        case 'amount_no_decimals': return number(0);
        case 'amount_with_comma_separator': return number(2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return number(0, '.', ',');
        case 'amount_no_decimals_with_space_separator': return number(0, ' ', ',');
        case 'amount_with_space_separator': return number(2, ' ', ',');
        case 'amount_with_period_and_space_separator': return number(2, ' ', '.');
        case 'amount_with_apostrophe_separator': return number(2, "'", '.');
        default: return number(2);
      }
    });
  }

  /* ---------- dialogs ---------- */

  function resolveDialog(target) {
    const el = typeof target === 'string' ? $(target) : target;
    return el instanceof HTMLDialogElement ? el : null;
  }

  function show(target) {
    const dialog = resolveDialog(target);
    if (!dialog || dialog.open) return dialog;
    if (typeof dialog.showModal === 'function') dialog.showModal();
    else dialog.setAttribute('open', '');
    dispatch('freddy:dialog:open', { id: dialog.id });
    return dialog;
  }

  function close(target) {
    const dialog = resolveDialog(target);
    if (dialog && dialog.open) dialog.close();
    return dialog;
  }

  // Native close (Escape, [data-close], backdrop, FREDDY.close) → one event for every path.
  document.addEventListener('close', (event) => {
    if (event.target instanceof HTMLDialogElement) dispatch('freddy:dialog:close', { id: event.target.id });
  }, true);

  /* ---------- header: mobile menu + mega menus ----------
     Every top-level item with sub-items = button.nav-mega[aria-controls] + its own div.mega[hidden].
     Click / Enter / Space toggle (touch, keyboard, mobile). header[data-mega="hover"] on a desktop pointer
     device (> 800px) also opens on hover: short open delay, another trigger switches at once, resting on a
     plain top-level link, the logo, the tools or the language switch closes, close delay after the pointer
     leaves the header. A mouse click on a hover-opened trigger opens the item's own page (data-href); without
     a page it pins the panel open ([data-mega-hover] marks hover-opened triggers; pinned panels survive the
     pointer leaving the header). Every panel also ends with a "shop all" link to that page. Tab goes from an open trigger into its panel, then on to the next trigger.
     State lives in the DOM (hidden / aria-expanded), so theme-editor re-renders need no re-init; timers only
     act on connected elements. */

  const MEGA_OPEN_DELAY = 100;
  const MEGA_CLOSE_DELAY = 250;
  const megaHoverQuery = window.matchMedia ? window.matchMedia('(hover: hover) and (pointer: fine) and (min-width: 801px)') : null;
  let megaOpenTimer = 0;
  let megaOpenTrigger = null;
  let megaCloseTimer = 0;

  function megaPanel(trigger) {
    const id = trigger && trigger.getAttribute('aria-controls');
    return id ? document.getElementById(id) : null;
  }

  function megaTrigger(panel) {
    return panel && panel.id ? $('.nav-mega[aria-controls="' + panel.id + '"]') : null;
  }

  function megaHoverHeader(target) {
    const header = target && target.closest('header[data-mega="hover"]');
    return header && megaHoverQuery && megaHoverQuery.matches ? header : null;
  }

  function cancelMegaTimers() {
    clearTimeout(megaOpenTimer);
    clearTimeout(megaCloseTimer);
    megaOpenTimer = 0;
    megaCloseTimer = 0;
    megaOpenTrigger = null;
  }

  function closeMega() {
    cancelMegaTimers();
    $$('header .mega:not([hidden])').forEach((panel) => { panel.hidden = true; });
    $$('.nav-mega').forEach((button) => {
      button.setAttribute('aria-expanded', 'false');
      button.removeAttribute('data-mega-hover');
    });
  }

  function openMega(trigger, byHover) {
    const panel = megaPanel(trigger);
    if (!panel) return;
    closeMega();
    panel.hidden = false;
    trigger.setAttribute('aria-expanded', 'true');
    if (byHover) trigger.setAttribute('data-mega-hover', '');
  }

  function toggleMega(trigger, event) {
    const panel = megaPanel(trigger);
    if (!panel) return;
    if (panel.hidden) openMega(trigger, false);
    else if (trigger.hasAttribute('data-mega-hover')) {
      // A real mouse click (detail > 0; keyboard/assistive "clicks" have detail 0) on a trigger whose panel the
      // hover already opened goes to the item's own page, like a link; touch, keyboard and click mode only toggle.
      if (trigger.dataset.href && event && event.detail > 0) {
        window.location.href = trigger.dataset.href;
        return;
      }
      cancelMegaTimers();
      trigger.removeAttribute('data-mega-hover');
    } else closeMega();
  }

  function openHoverTrigger(header) {
    const panel = header.querySelector('.mega:not([hidden])');
    const trigger = megaTrigger(panel);
    return trigger && trigger.hasAttribute('data-mega-hover') ? trigger : null;
  }

  document.addEventListener('pointerover', (event) => {
    if (event.pointerType && event.pointerType !== 'mouse') return;
    const target = asElement(event.target);
    const header = megaHoverHeader(target);
    if (!header) return;
    clearTimeout(megaCloseTimer);
    megaCloseTimer = 0;
    const trigger = target.closest('.nav-mega');
    if (trigger) {
      // moving inside the trigger (label <-> symbol span) is not entering it: a trigger just closed by a click stays closed
      const from = asElement(event.relatedTarget);
      if (from && trigger.contains(from)) return;
      const panel = megaPanel(trigger);
      if (!panel || !panel.hidden) {
        clearTimeout(megaOpenTimer);
        megaOpenTrigger = null;
        return;
      }
      if (header.querySelector('.mega:not([hidden])')) {
        openMega(trigger, true);
        return;
      }
      if (megaOpenTrigger === trigger) return;
      clearTimeout(megaOpenTimer);
      megaOpenTrigger = trigger;
      megaOpenTimer = setTimeout(() => {
        megaOpenTrigger = null;
        if (trigger.isConnected) openMega(trigger, true);
      }, MEGA_OPEN_DELAY);
      return;
    }
    clearTimeout(megaOpenTimer);
    megaOpenTrigger = null;
    // resting on a plain top-level link, the logo, the tools or the language switch closes (a sweep to the bag
    // no longer leaves the last trigger's panel open); the header area between the nav and the panel does not
    if (target.closest('nav > a, .brand, .shoplink, .header-tools, .language-switch') && header.querySelector('.mega:not([hidden])')) {
      megaCloseTimer = setTimeout(closeMega, MEGA_OPEN_DELAY);
    }
  });

  document.addEventListener('pointerout', (event) => {
    if (event.pointerType && event.pointerType !== 'mouse') return;
    const header = megaHoverHeader(asElement(event.target));
    if (!header) return;
    const to = asElement(event.relatedTarget);
    if (to && header.contains(to)) return;
    clearTimeout(megaOpenTimer);
    megaOpenTrigger = null;
    if (!openHoverTrigger(header)) return;
    clearTimeout(megaCloseTimer);
    megaCloseTimer = setTimeout(() => {
      if (openHoverTrigger(header)) closeMega();
    }, MEGA_CLOSE_DELAY);
  });

  // Tab order while a panel is open: trigger -> its panel -> the next trigger / tools (the panels sit after
  // .header-tools in the DOM). Leaving the header forwards continues after its last element, skipping the panel.
  const MEGA_FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]):not([type="hidden"]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

  function megaFocusables(root) {
    return $$(MEGA_FOCUSABLE, root).filter((el) => el.tabIndex >= 0 && el.getClientRects().length > 0);
  }

  function megaTabOrder(header) {
    const panels = $$('.mega', header);
    const order = [];
    megaFocusables(header).forEach((el) => {
      if (panels.some((panel) => panel.contains(el))) return;
      order.push(el);
      const panel = el.matches('.nav-mega') ? megaPanel(el) : null;
      if (panel && !panel.hidden && header.contains(panel)) order.push(...megaFocusables(panel));
    });
    return order;
  }

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Tab' || event.altKey || event.ctrlKey || event.metaKey) return;
    const active = asElement(document.activeElement);
    const header = active && active.closest('header');
    if (!header || !header.querySelector('.mega:not([hidden])')) return;
    const order = megaTabOrder(header);
    const index = order.indexOf(active);
    if (index < 0) return;
    const next = order[index + (event.shiftKey ? -1 : 1)];
    if (next) {
      event.preventDefault();
      next.focus();
    } else if (!event.shiftKey) {
      const all = megaFocusables(header);
      const last = all[all.length - 1];
      if (last && last !== active) last.focus(); // the default Tab action then moves on from there
    }
  });

  document.addEventListener('focusout', (event) => {
    const target = asElement(event.target);
    const header = target && target.closest('header');
    if (!header || !header.querySelector('.mega:not([hidden])')) return;
    const to = asElement(event.relatedTarget);
    if (to && !header.contains(to)) closeMega();
  });

  document.addEventListener('shopify:block:select', (event) => {
    const target = asElement(event.target);
    const panel = target && target.closest('.mega');
    const trigger = megaTrigger(panel);
    if (trigger) openMega(trigger, false);
  });

  document.addEventListener('shopify:block:deselect', (event) => {
    const target = asElement(event.target);
    if (target && target.closest('.mega')) closeMega();
  });

  function closeMenu() {
    const nav = document.getElementById('navigation');
    if (nav) nav.classList.remove('open');
    const menu = $('header .menu');
    if (menu) menu.setAttribute('aria-expanded', 'false');
  }

  document.addEventListener('click', (event) => {
    const target = asElement(event.target);
    if (!target) return;

    const menu = target.closest('header .menu');
    if (menu) {
      const open = menu.getAttribute('aria-expanded') !== 'true';
      menu.setAttribute('aria-expanded', String(open));
      const nav = document.getElementById('navigation');
      if (nav) nav.classList.toggle('open', open);
      closeMega();
      return;
    }

    const megaButton = target.closest('.nav-mega');
    if (megaButton) {
      toggleMega(megaButton, event);
      return;
    }

    if (!target.closest('header')) closeMega();

    const closer = target.closest('[data-close]');
    if (closer) {
      const dialog = closer.closest('dialog');
      if (dialog) dialog.close();
      return;
    }

    if (event.target instanceof HTMLDialogElement && event.target.open) {
      const rect = event.target.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) {
        event.target.close();
        return;
      }
    }

    if (target.closest('.search-open')) {
      const dialog = show('#search-dialog');
      const input = document.getElementById('search-input');
      if (dialog && input) input.focus();
      return;
    }

    if (target.closest('.bag-open')) show('#bag-dialog');
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const active = asElement(document.activeElement);
    const trigger = megaTrigger(active && active.closest('.mega:not([hidden])'));
    const inNav = !!(active && active.closest('#navigation.open'));
    closeMenu();
    closeMega();
    // focus was in a panel (or the mobile nav) that is now hidden: back to its trigger, else the menu button
    const menu = $('header .menu');
    if (trigger && trigger.getClientRects().length) trigger.focus();
    else if ((trigger || inNav) && menu && menu.getClientRects().length) menu.focus();
  });

  /* ---------- language switch (concept languages.js) ---------- */

  const LANGUAGE_KEY = 'freddy-language';

  function initLanguageLinks(root = document) {
    $$('[data-language]', root).forEach((link) => {
      try {
        const url = new URL(link.getAttribute('href'), location.href);
        url.search = location.search;
        url.hash = location.hash;
        link.href = url.href;
      } catch (error) { /* keep the server href */ }
    });
  }

  document.addEventListener('click', (event) => {
    const target = asElement(event.target);
    const link = target && target.closest('[data-language]');
    if (!link || !settings.languageRemember) return;
    try { localStorage.setItem(LANGUAGE_KEY, link.dataset.language); } catch (error) { /* storage unavailable */ }
  });

  function redirectRoot() {
    if (!settings.languageRedirectRoot || location.pathname !== '/') return;
    if (window.Shopify && window.Shopify.designMode) return;
    try {
      const saved = localStorage.getItem(LANGUAGE_KEY);
      const roots = settings.languageRoots || {};
      if (saved && saved !== settings.primaryLanguage && roots[saved] && roots[saved] !== '/') {
        location.replace(roots[saved] + location.search + location.hash);
      }
    } catch (error) { /* storage unavailable */ }
  }

  /* ---------- home chooser (02-home §7.6, data contract §E.2) ---------- */

  const readyChoosers = new WeakSet();

  function initChooser(root) {
    if (!root || readyChoosers.has(root)) return;
    const buttons = $$('[data-choice]', root);
    const result = root.querySelector('#choice-result');
    if (!result) return;
    readyChoosers.add(root);
    buttons.forEach((button) => {
      button.setAttribute('aria-pressed', 'false');
      button.addEventListener('click', () => {
        buttons.forEach((other) => other.setAttribute('aria-pressed', String(other === button)));
        result.replaceChildren();
        const title = document.createElement('strong');
        title.textContent = button.dataset.resultTitle || '';
        result.append(title, document.createElement('br'), document.createTextNode(button.dataset.resultText || ''));
        if (button.dataset.resultUrl) {
          const link = document.createElement('a');
          link.href = button.dataset.resultUrl;
          link.textContent = button.dataset.resultLabel || '';
          result.append(link);
        }
      });
    });
  }

  function initChoosers(root = document) {
    $$('.chooser', root).forEach(initChooser);
  }

  /* ---------- theme editor ---------- */

  let selectedSection = null;

  function dialogsIn(root) {
    return root ? $$('#search-dialog, #bag-dialog', root) : [];
  }

  document.addEventListener('shopify:section:load', (event) => {
    initLanguageLinks(event.target);
    initChoosers(event.target);
    if (window.instgrm && window.instgrm.Embeds) window.instgrm.Embeds.process();
    if (event.detail && event.detail.sectionId === selectedSection) dialogsIn(event.target).forEach(show);
  });

  document.addEventListener('shopify:section:select', (event) => {
    selectedSection = event.detail ? event.detail.sectionId : null;
    dialogsIn(event.target).forEach(show);
  });

  document.addEventListener('shopify:section:deselect', (event) => {
    if (event.detail && event.detail.sectionId === selectedSection) selectedSection = null;
    dialogsIn(event.target).forEach(close);
  });

  document.addEventListener('shopify:block:select', (event) => {
    const target = asElement(event.target);
    if (!target) return;
    if (target.matches('[data-choice]')) target.click();
    const dialog = target.closest('#search-dialog, #bag-dialog');
    if (dialog) show(dialog);
  });

  /* ---------- init ---------- */

  Object.assign(F, { show, close, closeMega, formatMoney, wrapperId });

  redirectRoot();
  initLanguageLinks();
  initChoosers();
})();

/* ========== Added Oct 2026 (adjustments): self-contained modules, each re-initialised on shopify:section:load ========== */

/* ---------- announcement bar (sections/freddy-announcement-bar.liquid) ---------- */
/* Announcement bar (promotions) — sections/freddy-announcement-bar.liquid (adjustments Oct 2026).
   Self-contained: rotation (.is-active + aria-hidden + inert), arrows, live countdowns
   (span[data-announce-end] = epoch seconds) and the close button (localStorage key = data-announce-key).
   One shared 1 s ticker serves every bar and stops when nothing needs it; bars are (re)initialised on
   load and on shopify:section:load, and dropped once they leave the DOM. Never hides a bar in the editor. */
(() => {
  'use strict';

  const BAR = '[data-announce]';
  const states = new Map();
  let ticker = null;

  const asElement = (target) => (target instanceof Element ? target : target && target.parentElement) || null;
  const pad = (value) => String(value).padStart(2, '0');
  const inEditor = (bar) => Boolean((window.Shopify && window.Shopify.designMode) || (bar && bar.hasAttribute('data-announce-design')));

  function isDismissed(key) {
    if (!key) return false;
    try { return Boolean(localStorage.getItem(key)); } catch (error) { return false; }
  }

  function remember(key) {
    if (!key) return;
    try { localStorage.setItem(key, '1'); } catch (error) { /* storage unavailable */ }
  }

  function rotating(state) {
    return state.autoplay && !state.stopped && state.messages.length > 1;
  }

  function updateCountdowns(state) {
    if (!state.countdowns.length) return;
    const now = Date.now();
    state.countdowns = state.countdowns.filter((el) => {
      const end = Number(el.getAttribute('data-announce-end')) * 1000;
      const left = Math.floor((end - now) / 1000);
      if (!el.isConnected || !Number.isFinite(left) || end <= 0 || left <= 0) {
        el.hidden = true;
        el.textContent = '';
        return false;
      }
      const days = Math.floor(left / 86400);
      const time = pad(Math.floor((left % 86400) / 3600)) + ':' + pad(Math.floor((left % 3600) / 60)) + ':' + pad(left % 60);
      el.textContent = days > 0 ? days + state.days + ' ' + time : time;
      el.hidden = false;
      return true;
    });
  }

  function show(state, index, userDriven) {
    const count = state.messages.length;
    if (!count) return;
    if (userDriven && state.track) state.track.setAttribute('aria-live', 'polite');
    state.index = ((index % count) + count) % count;
    state.messages.forEach((message, i) => {
      const active = i === state.index;
      message.classList.toggle('is-active', active);
      if (active) message.removeAttribute('aria-hidden');
      else message.setAttribute('aria-hidden', 'true');
      if ('inert' in message) message.inert = !active;
    });
    state.elapsed = 0;
  }

  function tick() {
    let busy = false;
    states.forEach((state, bar) => {
      if (!bar.isConnected || bar.hidden) {
        states.delete(bar);
        return;
      }
      updateCountdowns(state);
      // Focus is read from the DOM (focusin does not fire while the window itself is unfocused).
      if (rotating(state) && !state.hover && !state.editor && !bar.contains(document.activeElement)) {
        state.elapsed += 1;
        if (state.elapsed >= state.interval) show(state, state.index + 1, false);
      }
      if (state.countdowns.length || rotating(state)) busy = true;
    });
    if (!busy) stopTicker();
  }

  function stopTicker() {
    if (ticker) clearInterval(ticker);
    ticker = null;
  }

  function startTicker() {
    if (ticker) return;
    let needed = false;
    states.forEach((state) => { if (state.countdowns.length || rotating(state)) needed = true; });
    if (needed) ticker = setInterval(tick, 1000);
  }

  function init(bar) {
    if (!(bar instanceof HTMLElement) || states.has(bar)) return;
    if (bar.hasAttribute('data-announce-dismissible') && !inEditor(bar) && isDismissed(bar.getAttribute('data-announce-key'))) {
      bar.hidden = true;
      return;
    }
    if (inEditor(bar)) bar.hidden = false;
    const messages = Array.from(bar.querySelectorAll('[data-announce-message]'));
    const interval = parseInt(bar.getAttribute('data-announce-interval'), 10);
    const state = {
      messages,
      track: bar.querySelector('[data-announce-track]'),
      countdowns: Array.from(bar.querySelectorAll('[data-announce-end]')),
      days: bar.getAttribute('data-announce-days') || 'd',
      autoplay: bar.hasAttribute('data-announce-autoplay'),
      interval: Number.isFinite(interval) ? Math.min(Math.max(interval, 2), 30) : 5,
      index: Math.max(0, messages.findIndex((message) => message.classList.contains('is-active'))),
      elapsed: 0,
      hover: false,
      editor: false,
      stopped: false,
    };
    states.set(bar, state);
    show(state, state.index, false);
    updateCountdowns(state);

    bar.addEventListener('pointerenter', (event) => { if (event.pointerType === 'mouse') state.hover = true; });
    bar.addEventListener('pointerleave', () => { state.hover = false; state.elapsed = 0; });
    bar.addEventListener('focusout', (event) => {
      if (event.relatedTarget instanceof Node && bar.contains(event.relatedTarget)) return;
      state.elapsed = 0;
    });
    startTicker();
  }

  function initAll(root = document) {
    if (!root || typeof root.querySelectorAll !== 'function') return;
    if (root instanceof Element && root.matches(BAR)) init(root);
    root.querySelectorAll(BAR).forEach(init);
  }

  function stateFor(el) {
    const bar = el && el.closest ? el.closest(BAR) : null;
    if (!bar) return null;
    init(bar);
    return states.get(bar) || null;
  }

  document.addEventListener('click', (event) => {
    const target = asElement(event.target);
    const control = target && target.closest('[data-announce-prev],[data-announce-next],[data-announce-close]');
    const bar = control && control.closest(BAR);
    if (!bar) return;

    if (control.hasAttribute('data-announce-close')) {
      if (inEditor(bar)) return;
      const key = bar.getAttribute('data-announce-key');
      remember(key);
      // The same campaign placed twice (header group + template) closes everywhere at once.
      document.querySelectorAll(BAR).forEach((other) => {
        if (other !== bar && (!key || other.getAttribute('data-announce-key') !== key || !other.hasAttribute('data-announce-dismissible'))) return;
        other.hidden = true;
        states.delete(other);
      });
      return;
    }

    const state = stateFor(control);
    if (!state) return;
    state.stopped = true;
    show(state, state.index + (control.hasAttribute('data-announce-prev') ? -1 : 1), true);
  });

  /* ---------- theme editor ---------- */

  document.addEventListener('shopify:section:load', (event) => {
    initAll(asElement(event.target));
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const section = asElement(event.target);
    if (!section) return;
    states.forEach((state, bar) => { if (section.contains(bar)) states.delete(bar); });
  });

  document.addEventListener('shopify:block:select', (event) => {
    const target = asElement(event.target);
    const message = target && target.closest('[data-announce-message]');
    const state = stateFor(message);
    const index = state ? state.messages.indexOf(message) : -1;
    if (index < 0) return;
    state.editor = true;
    show(state, index, false);
  });

  document.addEventListener('shopify:block:deselect', (event) => {
    const target = asElement(event.target);
    const state = target && target.closest('[data-announce-message]') ? stateFor(target) : null;
    if (!state) return;
    state.editor = false;
    state.elapsed = 0;
    startTicker();
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll());
  else initAll();
})();

/* ---------- energy strip: theme-editor parking (sections/freddy-energy-strip.liquid) ---------- */
(() => {
  'use strict';
  /* Energy strip: in the theme editor, park the strip at its start while one of its segments is selected,
     so the selected segment (the first copy, which carries the editor attributes) stays in view. */
  const toggleEditing = (event, on) => {
    const strip = event.target instanceof Element ? event.target.closest('.energy-strip') : null;
    if (strip) strip.toggleAttribute('data-editing', on);
  };
  document.addEventListener('shopify:block:select', (event) => toggleEditing(event, true));
  document.addEventListener('shopify:block:deselect', (event) => toggleEditing(event, false));
})();

/* ---------- background video: hero (bold), collection head, WR.UP focus (snippets/freddy-video.liquid) ---------- */
/* FREDDY background video (snippets/freddy-video.liquid, used by sections/freddy-hero-bold.liquid,
   freddy-collection-head.liquid and freddy-wrup-focus.liquid).
   Markup: scope (.hero-image in the hero, any [data-freddy-video] element elsewhere) > video[data-hero-video="all|desktop|mobile"]
   + button[data-hero-video-toggle] (hidden until this runs).
   The videos have no autoplay attribute (reduced motion is honoured before/without JS and the video is not buffered
   for visitors who never play it): this starts them. It keeps one state per scope (data-hero-video-state = playing |
   paused on the scope), plays only the video for the current viewport (700px, the same breakpoint as the
   <source media> attributes and the CSS) and pauses the other, starts paused for prefers-reduced-motion (WCAG 2.2.2)
   and swaps the button's translated aria-label + icon. Playback starts once a scope is in view (IntersectionObserver,
   when available; immediately otherwise); a scope scrolled out of view is paused without changing its state and resumes
   when it comes back.
   Delegated click; re-initialised on shopify:section:load; any number of scopes per page. */
(() => {
  'use strict';

  const SCOPE = '.hero-image, [data-freddy-video]';
  const VIDEO = 'video[data-hero-video]';
  const TOGGLE = '[data-hero-video-toggle]';
  const NO_SOURCE = (window.HTMLMediaElement && window.HTMLMediaElement.NETWORK_NO_SOURCE) || 3;
  const query = (q) => (typeof window.matchMedia === 'function' ? window.matchMedia(q) : null);
  const phone = query('(max-width: 700px)');
  const calm = query('(prefers-reduced-motion: reduce)');

  const videosIn = (scope) => Array.from(scope.querySelectorAll(VIDEO));

  function current(list) {
    if (list.length < 2) return list[0] || null;
    const role = phone && phone.matches ? 'mobile' : 'desktop';
    return list.find((video) => video.dataset.heroVideo === role) || list[0];
  }

  function setState(scope, playing) {
    scope.dataset.heroVideoState = playing ? 'playing' : 'paused';
    const button = scope.querySelector(TOGGLE);
    if (!button) return;
    const label = playing ? button.dataset.labelPause : button.dataset.labelPlay;
    if (label) button.setAttribute('aria-label', label);
    button.classList.toggle('is-paused', !playing);
  }

  function sync(scope) {
    const list = videosIn(scope);
    const wanted = scope.dataset.heroVideoState === 'playing' && scope.dataset.heroVideoVisible !== 'false';
    const target = wanted ? current(list) : null;
    list.forEach((video) => {
      if (video !== target && !video.paused) video.pause();
    });
    if (!target) return;
    // No <source> matched when the page was parsed (the 700px breakpoint was crossed since): re-run source selection.
    if (target.networkState === NO_SOURCE) target.load();
    let attempt = null;
    try {
      attempt = target.play();
    } catch (error) {
      attempt = null;
    }
    if (attempt && typeof attempt.catch === 'function') {
      attempt.catch((error) => {
        // Autoplay refused (e.g. iOS Low Power Mode): show the play state so one tap starts it.
        if (error && error.name === 'NotAllowedError' && scope.isConnected) setState(scope, false);
      });
    }
  }

  const observer = typeof window.IntersectionObserver === 'function'
    ? new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const visible = entry.isIntersecting ? 'true' : 'false';
        if (entry.target.dataset.heroVideoVisible === visible) return;
        entry.target.dataset.heroVideoVisible = visible;
        sync(entry.target);
      });
    })
    : null;

  function setup(scope) {
    if (!(scope instanceof Element) || scope.dataset.heroVideoReady) return;
    const list = videosIn(scope);
    if (!list.length) return;
    scope.dataset.heroVideoReady = 'true';
    list.forEach((video) => {
      video.autoplay = false; // playback follows the scope state only
      video.muted = true; // muted inline video may start without a user gesture
    });
    const button = scope.querySelector(TOGGLE);
    if (button) button.hidden = false;
    setState(scope, !(calm && calm.matches));
    // The observer's first callback reports whether the scope is in view and starts it then, so a video further down
    // the page does not start (and download) until it is scrolled to.
    if (observer) observer.observe(scope);
    else sync(scope);
  }

  function setupAll(root) {
    const base = root && typeof root.querySelectorAll === 'function' ? root : document;
    base.querySelectorAll(SCOPE).forEach(setup);
  }

  // Only set-up scopes carry data-hero-video-ready (appending it to the SCOPE list would only qualify its last item).
  const eachReady = (fn) => document.querySelectorAll('[data-hero-video-ready]').forEach(fn);

  function onMediaChange(list, fn) {
    if (!list) return;
    if (typeof list.addEventListener === 'function') list.addEventListener('change', fn);
    else if (typeof list.addListener === 'function') list.addListener(fn);
  }

  document.addEventListener('click', (event) => {
    const origin = event.target instanceof Element ? event.target : null;
    const button = origin ? origin.closest(TOGGLE) : null;
    const scope = button ? button.closest(SCOPE) : null;
    if (!scope) return;
    setup(scope);
    setState(scope, scope.dataset.heroVideoState !== 'playing');
    sync(scope);
  });

  onMediaChange(phone, () => eachReady(sync));
  onMediaChange(calm, () => {
    if (!calm.matches) return;
    eachReady((scope) => {
      setState(scope, false);
      sync(scope);
    });
  });

  document.addEventListener('shopify:section:load', (event) => setupAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    if (!observer || !(event.target instanceof Element)) return;
    event.target.querySelectorAll(SCOPE).forEach((scope) => observer.unobserve(scope));
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => setupAll(document));
  else setupAll(document);
})();
